import React, { useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Create, GetProductsList } from "adminApi/LaundryProductApi/LaundryProduct";
import { validateForm } from "utils/validation";
import { LaundryProductRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import AddLaundryProductForm from "./Componates/AddLaundryProductForm";
import fields from "../json/LaundryProductFields.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchAllData from "Hooks/useFetchAllData";

const AddLaundryProduct = () => {
  const navigate = useNavigate();
  const { formdata } = fields;

  const initialValues = formdata.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {});

  const { values, errors, handleChange, setErrors, setValues } = useFormChange(
    initialValues,
    LaundryProductRules
  );

  // Fetch products_list for the product_name dropdown
  const { data: productsList = [], loading: loadingProducts } = useFetchAllData(GetProductsList);

  // Dropdown options — show only product_name (no qty in label)
  const productOptions = useMemo(
    () => productsList.map((p) => ({
      value: p.product_name,
      label: p.product_name,
      available_quantity: p.available_quantity, // carried for logic below
    })),
    [productsList]
  );

  // When product_name changes → auto-fill ready_quantity with available_quantity
  useEffect(() => {
    if (!values.product_name) {
      setValues((prev) => ({ ...prev, ready_quantity: "", in_laundry_quantity: "", total_quantity: "" }));
      return;
    }
    const selected = productsList.find((p) => p.product_name === values.product_name);
    if (selected) {
      setValues((prev) => ({
        ...prev,
        ready_quantity: selected.available_quantity,
        in_laundry_quantity: prev.in_laundry_quantity || "",
        total_quantity: selected.available_quantity,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.product_name]);

  // When in_laundry_quantity changes → ready_quantity = available - in_laundry
  // Block: in_laundry_quantity cannot exceed available_quantity
  useEffect(() => {
    if (!values.product_name) return;
    const selected = productsList.find((p) => p.product_name === values.product_name);
    if (!selected) return;

    const inLaundry = parseInt(values.in_laundry_quantity) || 0;

    // Clamp: cannot send more to laundry than what's available
    if (inLaundry > selected.available_quantity) {
      setValues((prev) => ({
        ...prev,
        in_laundry_quantity: selected.available_quantity,
        ready_quantity: 0,
        total_quantity: selected.available_quantity,
      }));
      return;
    }

    const newReady = selected.available_quantity - inLaundry;
    const newTotal = inLaundry + newReady;

    setValues((prev) => ({
      ...prev,
      ready_quantity: newReady,
      total_quantity: newTotal,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.in_laundry_quantity]);

  const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
    async (formValues) => {
      const formData = new FormData();
      Object.entries(formValues).forEach(([key, val]) => formData.append(key, val ?? ""));
      return await Create({ data: formData });
    },
    (vals) => {
      const err = validateForm(vals, LaundryProductRules);
      setErrors(err);
      return err;
    },
    () => navigate(-1),
    "Laundry Product Created Successfully"
  );

  return (
    <>
      {(loadingSubmit || loadingProducts) && <Spinner isSpinner />}
      <AddLaundryProductForm
        fields={formdata}
        values={values}
        errors={errors}
        handleChange={handleChange}
        handleSubmit={(e) => handleSubmit(e, values)}
        loading={loadingSubmit}
        productOptions={productOptions}
      />
    </>
  );
};

export default AddLaundryProduct;
